import type { DeviceType } from '@ukrop-nm/shared';

/** Normalize MAC to AA:BB:CC:DD:EE:FF or null if invalid. */
export function normalizeMac(input: string | undefined | null): string | null {
  if (!input) return null;
  const hex = input.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
  if (hex.length !== 12) return null;
  return hex.match(/.{1,2}/g)!.join(':');
}

/** Tiny public OUI subset for vendor hint (local only). */
const OUI_VENDORS: Array<{ prefix: string; vendor: string }> = [
  { prefix: '48:8F:5A', vendor: 'MikroTik' },
  { prefix: '4C:5E:0C', vendor: 'MikroTik' },
  { prefix: '64:D1:54', vendor: 'MikroTik' },
  { prefix: '6C:3B:6B', vendor: 'MikroTik' },
  { prefix: 'C4:AD:34', vendor: 'MikroTik' },
  { prefix: 'D4:CA:6D', vendor: 'MikroTik' },
  { prefix: 'E4:8D:8C', vendor: 'MikroTik' },
  { prefix: '2C:C8:1B', vendor: 'RouterBOARD / MikroTik' },
  { prefix: '00:0C:29', vendor: 'VMware' },
  { prefix: '00:50:56', vendor: 'VMware' },
  { prefix: '00:1A:2F', vendor: 'Cisco' },
  { prefix: '00:1B:0D', vendor: 'Cisco' },
  { prefix: '00:25:90', vendor: 'Cisco' },
  { prefix: 'F4:CF:E2', vendor: 'Cisco' },
  { prefix: 'B8:27:EB', vendor: 'Raspberry Pi' },
  { prefix: 'DC:A6:32', vendor: 'Raspberry Pi' },
  { prefix: 'E4:5F:01', vendor: 'Raspberry Pi' },
  { prefix: '00:1E:C2', vendor: 'Apple' },
  { prefix: '3C:22:FB', vendor: 'Apple' },
  { prefix: 'A4:83:E7', vendor: 'Apple' },
  { prefix: 'F0:18:98', vendor: 'Apple' },
  { prefix: '00:1A:11', vendor: 'Google' },
  { prefix: '3C:5A:B4', vendor: 'Google' },
  { prefix: '00:17:88', vendor: 'Philips Hue' },
  { prefix: 'B0:BE:76', vendor: 'TP-Link' },
  { prefix: '50:C7:BF', vendor: 'TP-Link' },
  { prefix: '98:DA:C4', vendor: 'TP-Link' },
  { prefix: '00:1D:7E', vendor: 'Cisco-Linksys' },
  { prefix: '00:24:B2', vendor: 'Netgear' },
  { prefix: '28:C6:8E', vendor: 'Netgear' },
  { prefix: '00:15:6D', vendor: 'Ubiquiti' },
  { prefix: '24:5A:4C', vendor: 'Ubiquiti' },
  { prefix: 'FC:EC:DA', vendor: 'Ubiquiti' },
  { prefix: '00:0D:B9', vendor: 'PC Engines' },
  { prefix: '00:90:0B', vendor: 'Handlink / industrial' },
  { prefix: '00:E0:4C', vendor: 'Realtek' },
  { prefix: '52:54:00', vendor: 'QEMU/KVM' },
];

export function resolveOuiVendor(mac: string | undefined | null): string | null {
  const normalized = normalizeMac(mac);
  if (!normalized) return null;
  const oui = normalized.slice(0, 8);
  return OUI_VENDORS.find((e) => e.prefix === oui)?.vendor ?? null;
}

export type AppearanceId =
  | 'mikrotik_rb2011'
  | 'mikrotik_switch'
  | 'mikrotik_hap'
  | 'rack_switch'
  | 'firewall_appliance'
  | 'tower_server'
  | 'desktop_pc'
  | 'laptop'
  | 'tablet'
  | 'phone'
  | 'display'
  | 'printer'
  | 'camera'
  | 'nas'
  | 'modem'
  | 'starlink'
  | 'lte_dish'
  | 'access_point'
  | 'ups'
  | 'power_station'
  | 'generic';

export type DeviceAppearance = {
  id: AppearanceId;
  title: string;
  subtitle: string;
  matchedBy: 'photo' | 'model' | 'label' | 'vendor' | 'type' | 'cached';
};

type ModelRule = { match: RegExp; id: AppearanceId; title: string; subtitle: string };

const MODEL_RULES: ModelRule[] = [
  {
    match: /rb2011|2011ui/i,
    id: 'mikrotik_rb2011',
    title: 'MikroTik RB2011',
    subtitle: '1U-подібний роутер, багато Ethernet портів',
  },
  {
    match: /rb4011|rb5009|ccr|hex\b|hap\s*ax|hap\s*ac|hap\s*lite|cappy/i,
    id: 'mikrotik_hap',
    title: 'MikroTik роутер / AP',
    subtitle: 'Компактний корпус з антенами або без',
  },
  {
    match: /crs|css|rb260|rb1100/i,
    id: 'mikrotik_switch',
    title: 'MikroTik switch',
    subtitle: 'Металевий комутатор, ряд портів спереду',
  },
  {
    match: /catalyst|nexus|cisco\s*sg|cbs2|sg300|sg350/i,
    id: 'rack_switch',
    title: 'Rack switch',
    subtitle: 'Стійковий комутатор 19″',
  },
  {
    match: /fortigate|pfsense|opnsense|sophos|asa\b|firepower/i,
    id: 'firewall_appliance',
    title: 'Firewall appliance',
    subtitle: 'Мережевий екран у металевому корпусі',
  },
  {
    match: /dell\s*r|hp\s*dl|proliant|poweredge|supermicro|ryzen|xeon/i,
    id: 'tower_server',
    title: 'Сервер',
    subtitle: 'Стійковий або tower-сервер',
  },
  {
    match: /laptop|notebook|thinkpad|macbook|latitude|elitebook/i,
    id: 'laptop',
    title: 'Ноутбук',
    subtitle: 'Портативний ПК',
  },
  {
    match: /ipad|tablet|galaxy\s*tab/i,
    id: 'tablet',
    title: 'Планшет',
    subtitle: 'Планшетний пристрій',
  },
  {
    match: /iphone|android|pixel|galaxy\s*s|yealink|grandstream/i,
    id: 'phone',
    title: 'Телефон',
    subtitle: 'IP-телефон або смартфон',
  },
  {
    match: /printer|laserjet|epson|brother|canon/i,
    id: 'printer',
    title: 'Принтер',
    subtitle: 'Офісний друкувальний пристрій',
  },
  {
    match: /hikvision|dahua|axis|reolink|camera|cam\b/i,
    id: 'camera',
    title: 'Камера',
    subtitle: 'IP-камера відеоспостереження',
  },
  {
    match: /synology|qnap|truenas|nas\b/i,
    id: 'nas',
    title: 'NAS',
    subtitle: 'Мережеве сховище',
  },
  {
    match: /\b(cAP|wAP)\b|unifi|\bu6[- ]|\bu7[- ]|nanohd|\beap\d|omada|air-cap|access\s*point|точка\s*доступу/i,
    id: 'access_point',
    title: 'Точка доступу',
    subtitle: 'Стельова / настінна / зовнішня AP',
  },
  {
    match: /lhg\s*lte|sxt\s*lte|lte\s*(dish|cpe|antenna|антен|тар[еє]л)|4g\s*(dish|antenna|антен)|5g\s*(dish|antenna|антен)|mimo\s*(lte|4g|5g)|lte\s*mimo|панел[ьн].*(lte|4g)/i,
    id: 'lte_dish',
    title: 'LTE антена',
    subtitle: 'Тарілка / панельна MIMO-антена',
  },
  {
    match: /starlink|dishy|vsat|satellite|gen\s*3\s*dish/i,
    id: 'starlink',
    title: 'Starlink / супутниковий термінал',
    subtitle: 'Прямокутна антена (dish) на стійці',
  },
  {
    match: /modem|huawei\s*b\d|zte\s*mf|ont\b|cpe\b/i,
    id: 'modem',
    title: 'Модем / CPE',
    subtitle: 'WAN-доступ (LTE / xDSL / ONT)',
  },
  {
    match: /ecoflow|bluetti|jackery|anker\s*solix|delta\b|river\s*\d|power\s*station|зарядн|портативн.*(дбж|ups)|portable\s*(ups|power)/i,
    id: 'power_station',
    title: 'Зарядна станція',
    subtitle: 'Портативний ДБЖ / power station',
  },
  {
    match: /apc|eaton|ups\b|дбж/i,
    id: 'ups',
    title: 'ДБЖ',
    subtitle: 'Джерело безперебійного живлення',
  },
];

const TYPE_FALLBACK: Record<DeviceType, { id: AppearanceId; title: string; subtitle: string }> = {
  switch: { id: 'rack_switch', title: 'Комутатор', subtitle: 'Мережевий switch' },
  router: { id: 'mikrotik_rb2011', title: 'Маршрутизатор', subtitle: 'Мережевий роутер' },
  wifi_router: { id: 'mikrotik_hap', title: 'Wi‑Fi маршрутизатор', subtitle: 'SOHO-роутер з Wi‑Fi' },
  access_point: { id: 'access_point', title: 'Точка доступу', subtitle: 'Wi‑Fi AP (PoE)' },
  firewall: { id: 'firewall_appliance', title: 'Мережевий екран', subtitle: 'Firewall appliance' },
  server: { id: 'tower_server', title: 'Сервер', subtitle: 'Обчислювальний вузол' },
  desktop: { id: 'desktop_pc', title: 'ПК', subtitle: 'Робоча станція' },
  laptop: { id: 'laptop', title: 'Ноутбук', subtitle: 'Портативний ПК' },
  tablet: { id: 'tablet', title: 'Планшет', subtitle: 'Планшетний пристрій' },
  display: { id: 'display', title: 'Дисплей', subtitle: 'Монітор / TV / панель' },
  ip_phone: { id: 'phone', title: 'IP-телефон', subtitle: 'Офісний телефон' },
  wifi_phone: { id: 'phone', title: 'Wi‑Fi телефон', subtitle: 'Бездротовий телефон' },
  printer: { id: 'printer', title: 'Принтер', subtitle: 'Друкувальний пристрій' },
  camera: { id: 'camera', title: 'Камера', subtitle: 'IP-камера' },
  nas: { id: 'nas', title: 'NAS', subtitle: 'Мережеве сховище' },
  modem: { id: 'modem', title: 'Модем', subtitle: 'WAN CPE' },
  lte_antenna: { id: 'lte_dish', title: 'LTE антена', subtitle: 'Тарілка / MIMO-панель' },
  satellite: { id: 'starlink', title: 'Супутниковий термінал', subtitle: 'Starlink / VSAT' },
  cloud: { id: 'generic', title: 'Хмара', subtitle: 'Логічний / зовнішній сервіс' },
  iot: { id: 'generic', title: 'IoT', subtitle: 'Вбудований пристрій' },
  ups: { id: 'ups', title: 'ДБЖ', subtitle: 'Живлення' },
  power_station: {
    id: 'power_station',
    title: 'Зарядна станція',
    subtitle: 'Портативний ДБЖ',
  },
  controller: { id: 'generic', title: 'Контролер', subtitle: 'Керуючий вузол' },
  zone: { id: 'generic', title: 'Зона', subtitle: 'Логічна група' },
  generic: { id: 'generic', title: 'Пристрій', subtitle: 'Загальний вигляд' },
};

const APPEARANCE_IDS = new Set<string>(
  MODEL_RULES.map((r) => r.id).concat(['desktop_pc', 'display', 'generic', 'starlink', 'lte_dish', 'access_point']),
);

export function isAppearanceId(value: string | undefined | null): value is AppearanceId {
  return Boolean(value && APPEARANCE_IDS.has(value));
}

function matchModelRules(text: string): ModelRule | null {
  const t = text.trim();
  if (!t) return null;
  for (const rule of MODEL_RULES) {
    if (rule.match.test(t)) return rule;
  }
  return null;
}

export function resolveDeviceAppearance(input: {
  label?: string | null;
  model?: string | null;
  vendor?: string | null;
  macLocal?: string | null;
  type: DeviceType;
  hasPhoto?: boolean;
  appearanceId?: string | null;
}): DeviceAppearance {
  if (input.hasPhoto) {
    return {
      id: 'generic',
      title: input.model?.trim() || input.label?.trim() || 'Власне фото',
      subtitle: 'Завантажене зображення пристрою',
      matchedBy: 'photo',
    };
  }

  const model = input.model?.trim() ?? '';
  const label = input.label?.trim() ?? '';
  const haystack = [model, label].filter(Boolean).join(' ');

  const fromModel = matchModelRules(model);
  if (fromModel) {
    return {
      id: fromModel.id,
      title: fromModel.title,
      subtitle: fromModel.subtitle,
      matchedBy: 'model',
    };
  }

  const fromLabel = matchModelRules(label);
  if (fromLabel) {
    return {
      id: fromLabel.id,
      title: fromLabel.title,
      subtitle: fromLabel.subtitle,
      matchedBy: 'label',
    };
  }

  const fromBoth = matchModelRules(haystack);
  if (fromBoth) {
    return {
      id: fromBoth.id,
      title: fromBoth.title,
      subtitle: fromBoth.subtitle,
      matchedBy: model ? 'model' : 'label',
    };
  }

  if (isAppearanceId(input.appearanceId)) {
    const fb = TYPE_FALLBACK[input.type] ?? TYPE_FALLBACK.generic;
    const rule = MODEL_RULES.find((r) => r.id === input.appearanceId);
    return {
      id: input.appearanceId,
      title: rule?.title ?? (model || label || fb.title),
      subtitle: rule?.subtitle ?? 'Збережений малюнок з назви / моделі',
      matchedBy: 'cached',
    };
  }

  const vendor = input.vendor?.trim() || resolveOuiVendor(input.macLocal) || '';
  if (/mikrotik|routerboard/i.test(vendor) || /mikrotik|routerboard|rb\d/i.test(haystack)) {
    if (/switch|crs|css/i.test(haystack) || input.type === 'switch') {
      return {
        id: 'mikrotik_switch',
        title: `${vendor || 'MikroTik'} switch`,
        subtitle: model || label || 'Комутатор MikroTik',
        matchedBy: vendor ? 'vendor' : 'label',
      };
    }
    return {
      id: 'mikrotik_rb2011',
      title: vendor || 'MikroTik',
      subtitle: model || label || 'Маршрутизатор MikroTik',
      matchedBy: vendor ? 'vendor' : 'label',
    };
  }

  const fb = TYPE_FALLBACK[input.type] ?? TYPE_FALLBACK.generic;
  return { ...fb, matchedBy: 'type' };
}

/** Rebuild silhouette from device name + model; drops custom photo. */
export function refreshAppearanceFromNameModel(input: {
  label?: string | null;
  model?: string | null;
  vendor?: string | null;
  macLocal?: string | null;
  type: DeviceType;
}): DeviceAppearance {
  return resolveDeviceAppearance({
    ...input,
    hasPhoto: false,
    appearanceId: null,
  });
}
